import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Switch,
    FormControlLabel,
    Alert,
    CircularProgress,
    Chip,
    Grid,
    Card,
    CardContent,
    Divider,
    useTheme,
    useMediaQuery,
    Stack,
    Collapse,
    CardActions,
    Tabs,
    Tab
} from '@mui/material';
import {
    Add,
    Edit,
    Delete,
    Save,
    Cancel,
    CloudUpload,
    ExpandMore,
    ExpandLess,
    SubdirectoryArrowRight,
    AddCircleOutline,
    CheckCircle,
    Block
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import {
    getCategories,
    createCategory,
    updateCategory,
    deleteCategory,
    clearError,
    clearSuccess,
} from '../store/slices/categorySlice';

const Categories = () => {
    const dispatch = useDispatch();
    const { categories, isLoading, error, success } = useSelector((state) => state.categories);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [tabValue, setTabValue] = useState(0); // 0: Active, 1: Pending

    const [openDialog, setOpenDialog] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        slug: '',
        description: '',
        parentCategory: '',
        isActive: true,
        sortOrder: 0,
        metaTitle: '',
        metaDescription: '',
        keywords: [],
    });
    const [newKeyword, setNewKeyword] = useState('');
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [expandedCategories, setExpandedCategories] = useState({});
    const [addingSubcategoryFor, setAddingSubcategoryFor] = useState(null);

    const parentCategories = useMemo(() => {
        const parents = categories.filter(cat => !cat.parentCategory);

        if (tabValue === 0) {
            // Active Tab: Show Active/Approved Parents
            // We treat missing status as 'approved' for legacy compatibility
            return parents.filter(cat => cat.isActive && (cat.status === 'approved' || !cat.status));
        } else {
            // Pending Tab: Show Pending Parents OR Parents containing Pending Subs
            return parents.filter(cat => {
                const isPendingSelf = cat.status === 'pending';
                const hasPendingSub = categories.some(sub =>
                    (sub.parentCategory === cat._id || sub.parentCategory?._id === cat._id) &&
                    sub.status === 'pending'
                );
                return isPendingSelf || hasPendingSub;
            });
        }
    }, [categories, tabValue]);

    const getSubcategories = useCallback((parentId) => {
        const subs = categories.filter(cat =>
            (cat.parentCategory === parentId || cat.parentCategory?._id === parentId)
        );

        if (tabValue === 0) {
            return subs.filter(cat => cat.isActive && (cat.status === 'approved' || !cat.status));
        } else {
            return subs.filter(cat => cat.status === 'pending');
        }
    }, [categories, tabValue]);

    const getCategoryImageUrl = useCallback((category) => {
        if (!category?.image) return 'https://via.placeholder.com/50x50?text=No+Img';

        // Use Cloudinary URL directly
        if (category.image.startsWith('http')) return category.image;

        // For local images, remove '/api' from the base URL if present
        let baseUrl = process.env.REACT_APP_API_URL || 'https://xaintro-backend.onrender.com';
        baseUrl = baseUrl.replace(/\/api\/?$/, ''); // Remove trailing /api or /api/

        return `${baseUrl}${category.image.startsWith('/') ? '' : '/'}${category.image}`;
    }, []);

    useEffect(() => {
        // Fetch all categories to ensure we have the full tree structure for filtering
        dispatch(getCategories('?all=true'));
    }, [dispatch]);

    useEffect(() => {
        if (success && openDialog) handleCloseDialog();
    }, [success, openDialog]);

    const toggleExpand = useCallback((categoryId) => {
        setExpandedCategories(prev => ({
            ...prev,
            [categoryId]: !prev[categoryId]
        }));
    }, []);

    const handleOpenDialog = useCallback((category = null) => {
        dispatch(clearError());
        dispatch(clearSuccess());

        if (category) {
            setEditingCategory(category);
            setFormData({
                name: category.name || '',
                slug: category.slug || '',
                description: category.description || '',
                parentCategory: category.parentCategory?._id || category.parentCategory || '',
                isActive: category.isActive !== undefined ? category.isActive : true,
                sortOrder: category.sortOrder || 0,
                metaTitle: category.metaTitle || '',
                metaDescription: category.metaDescription || '',
                keywords: category.keywords || [],
            });
            setImagePreview(getCategoryImageUrl(category));
        } else {
            setEditingCategory(null);
            setFormData({
                name: '',
                slug: '',
                description: '',
                parentCategory: '',
                isActive: true,
                sortOrder: 0,
                metaTitle: '',
                metaDescription: '',
                keywords: [],
            });
            setImagePreview(null);
        }
        setImageFile(null);
        setOpenDialog(true);
    }, [dispatch, getCategoryImageUrl]);

    const handleCloseDialog = useCallback(() => {
        setOpenDialog(false);
        setEditingCategory(null);
        setAddingSubcategoryFor(null);
        setFormData({
            name: '',
            slug: '',
            description: '',
            parentCategory: '',
            isActive: true,
            sortOrder: 0,
            metaTitle: '',
            metaDescription: '',
            keywords: [],
        });
        setNewKeyword('');
        setImageFile(null);
        setImagePreview(null);
        dispatch(clearError());
    }, [dispatch]);

    const handleChange = useCallback((e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    }, []);

    const handleNameChange = useCallback((e) => {
        const name = e.target.value;
        const slug = name
            .toLowerCase()
            .replace(/[^a-z0-9 -]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .trim('-');
        setFormData(prev => ({ ...prev, name, slug }));
    }, []);

    const handleAddKeyword = useCallback(() => {
        if (newKeyword.trim() && !formData.keywords.includes(newKeyword.trim())) {
            setFormData(prev => ({
                ...prev,
                keywords: [...prev.keywords, newKeyword.trim()]
            }));
            setNewKeyword('');
        }
    }, [newKeyword, formData.keywords]);

    const handleRemoveKeyword = useCallback((keywordToRemove) => {
        setFormData(prev => ({
            ...prev,
            keywords: prev.keywords.filter(keyword => keyword !== keywordToRemove)
        }));
    }, []);

    const handleImageChange = useCallback((event) => {
        const file = event.target.files[0];
        if (file && file.type.startsWith('image/')) {
            setImageFile(file);
            setImagePreview(URL.createObjectURL(file));
        }
    }, []);

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        dispatch(clearError());
        setUploadingImage(true);

        try {
            let imageUrl = formData.image;

            if (imageFile) {
                const formDataToUpload = new FormData();
                formDataToUpload.append('image', imageFile);

                const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://xaintro-backend.onrender.com'}/upload`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                    body: formDataToUpload
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.message || 'Image upload failed');
                }

                const result = await response.json();
                imageUrl = result.imageUrl;
            }

            const submitData = { ...formData, image: imageUrl, sortOrder: Number(formData.sortOrder) };

            if (editingCategory?._id) {
                dispatch(updateCategory({ id: editingCategory._id, categoryData: submitData }));
            } else {
                dispatch(createCategory(submitData));
            }
        } catch (error) {
            console.error('Submit error:', error);
            alert(`Error: ${error.message}`);
        } finally {
            setUploadingImage(false);
        }
    }, [dispatch, formData, imageFile, editingCategory]);

    const handleAddSubcategory = useCallback((parentCategory) => {
        setAddingSubcategoryFor(parentCategory);
        setEditingCategory(null);
        setFormData({
            name: '',
            slug: '',
            description: '',
            parentCategory: parentCategory._id,
            isActive: true,
            sortOrder: 0,
            metaTitle: '',
            metaDescription: '',
            keywords: [],
        });
        setImagePreview(null);
        setImageFile(null);
        setOpenDialog(true);
    }, []);

    const handleDelete = useCallback((categoryId) => {
        if (window.confirm('Are you sure? This will delete the category and all subcategories.')) {
            dispatch(deleteCategory(categoryId)).then(() => {
                dispatch(getCategories('?all=true'));
            });
        }
    }, [dispatch]);

    const handleVerify = useCallback(async (categoryId, action) => {
        if (window.confirm(`Are you sure you want to ${action} this category?`)) {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://xaintro-backend.onrender.com'}/categories/${categoryId}/verify`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    },
                    body: JSON.stringify({ action })
                });

                if (response.ok) {
                    // Refresh all categories to keep client-side filtering working
                    dispatch(getCategories('?all=true'));
                } else {
                    alert('Failed to verify');
                }
            } catch (error) {
                console.error('Verification error', error);
            }
        }
    }, [dispatch]);

    if (isLoading && categories.length === 0) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography
                    variant="h4"
                    sx={{ color: '#000', fontWeight: 700, pb: 2, borderBottom: '2px solid #ff2852' }}
                >
                    Categories Management
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => handleOpenDialog()}
                    sx={{ backgroundColor: '#ff2852', color: 'white', fontWeight: 600, '&:hover': { backgroundColor: '#5A189A' } }}
                >
                    Add Category
                </Button>
            </Box>

            <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 3 }}>
                <Tab label="Active / Approved" />
                <Tab label="Pending Approval" />
            </Tabs>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => dispatch(clearError())}>
                    {error}
                </Alert>
            )}

            {/* Stats */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ border: '1px solid #e0e0e0', borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', transition: 'all 0.3s ease', '&:hover': { boxShadow: '0 4px 16px rgba(212,175,55,0.2)', borderColor: '#ff2852' } }}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography sx={{ color: '#666', fontWeight: 500 }} gutterBottom>
                                Total Categories
                            </Typography>
                            <Typography variant="h4" sx={{ color: '#000', fontWeight: 700 }}>
                                {categories.length}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ border: '1px solid #e0e0e0', borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', transition: 'all 0.3s ease', '&:hover': { boxShadow: '0 4px 16px rgba(212,175,55,0.2)', borderColor: '#ff2852' } }}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography sx={{ color: '#666', fontWeight: 500 }} gutterBottom>
                                Parent Categories
                            </Typography>
                            <Typography variant="h4" sx={{ color: '#000', fontWeight: 700 }}>
                                {categories.filter(c => !c.parentCategory).length}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ border: '1px solid #e0e0e0', borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', transition: 'all 0.3s ease', '&:hover': { boxShadow: '0 4px 16px rgba(212,175,55,0.2)', borderColor: '#ff2852' } }}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography sx={{ color: '#666', fontWeight: 500 }} gutterBottom>
                                Subcategories
                            </Typography>
                            <Typography variant="h4" sx={{ color: '#000', fontWeight: 700 }}>
                                {categories.filter(c => c.parentCategory).length}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ border: '1px solid #e0e0e0', borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', transition: 'all 0.3s ease', '&:hover': { boxShadow: '0 4px 16px rgba(212,175,55,0.2)', borderColor: '#ff2852' } }}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography sx={{ color: '#666', fontWeight: 500 }} gutterBottom>
                                Pending Approval
                            </Typography>
                            <Typography variant="h4" sx={{ color: '#d32f2f', fontWeight: 700 }}>
                                {categories.filter(c => c.status === 'pending').length}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Content Switch: Mobile Cards vs Desktop Table */}
            {isMobile ? (
                <Stack spacing={2}>
                    {parentCategories.map((category) => {
                        const subcategories = getSubcategories(category._id);
                        const isExpanded = expandedCategories[category._id];
                        const hasSubcategories = subcategories.length > 0;

                        return (
                            <Card key={category._id} sx={{
                                borderRadius: 2,
                                border: '1px solid #e0e0e0',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                            }}>
                                <Box p={2}>
                                    <Box display="flex" gap={2} alignItems="flex-start">
                                        <Box
                                            component="img"
                                            src={getCategoryImageUrl(category)}
                                            alt={category.name}
                                            sx={{
                                                width: 60,
                                                height: 60,
                                                objectFit: 'cover',
                                                borderRadius: 1,
                                                border: '1px solid #e0e0e0',
                                                backgroundColor: '#f5f5f5',
                                                flexShrink: 0
                                            }}
                                        />
                                        <Box flex={1}>
                                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                                                <Typography variant="h6" fontWeight={600} fontSize="1rem">
                                                    {category.name}
                                                </Typography>
                                                {hasSubcategories && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => toggleExpand(category._id)}
                                                        sx={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
                                                    >
                                                        <ExpandMore />
                                                    </IconButton>
                                                )}
                                            </Box>

                                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" gap={0.5} mb={1}>
                                                <Chip label="Parent" color="primary" size="small" sx={{ height: 20, fontSize: '0.65rem' }} />
                                                <Chip
                                                    label={category.isActive ? 'Active' : 'Inactive'}
                                                    color={category.isActive ? 'success' : 'default'}
                                                    size="small"
                                                    sx={{ height: 20, fontSize: '0.65rem' }}
                                                />
                                                <Chip label={`${category.productCount || 0} Products`} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                                            </Stack>

                                            <Typography variant="body2" color="text.secondary" noWrap sx={{ mb: 1 }}>
                                                {category.description || 'No description'}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <Divider sx={{ my: 1 }} />

                                    <Box display="flex" justifyContent="flex-end" gap={1}>
                                        {tabValue === 1 ? (
                                            <>
                                                <IconButton size="small" onClick={() => handleVerify(category._id, 'approve')} sx={{ color: 'green', bgcolor: 'rgba(0, 128, 0, 0.05)' }} title="Approve">
                                                    <CheckCircle fontSize="small" />
                                                </IconButton>
                                                <IconButton size="small" onClick={() => handleVerify(category._id, 'reject')} sx={{ color: 'red', bgcolor: 'rgba(255, 0, 0, 0.05)' }} title="Reject">
                                                    <Block fontSize="small" />
                                                </IconButton>
                                            </>
                                        ) : (
                                            <Button
                                                size="small"
                                                startIcon={<AddCircleOutline />}
                                                onClick={() => handleAddSubcategory(category)}
                                                sx={{ color: '#4caf50', fontSize: '0.75rem' }}
                                            >
                                                Add Sub
                                            </Button>
                                        )}
                                        <IconButton size="small" onClick={() => handleOpenDialog(category)} sx={{ color: '#ff2852', bgcolor: 'rgba(255, 40, 82, 0.05)' }}>
                                            <Edit fontSize="small" />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => handleDelete(category._id)} sx={{ color: '#d32f2f', bgcolor: 'rgba(211, 47, 47, 0.05)' }}>
                                            <Delete fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </Box>

                                {/* Subcategories */}
                                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                    <Box sx={{ bgcolor: '#fafafa', p: 1, borderTop: '1px solid #f0f0f0' }}>
                                        <Typography variant="caption" sx={{ px: 1, color: '#666', fontWeight: 600, display: 'block', mb: 1 }}>
                                            SUBCATEGORIES ({subcategories.length})
                                        </Typography>
                                        <Stack spacing={1}>
                                            {subcategories.map((subcat) => (
                                                <Paper key={subcat._id} sx={{ p: 1.5, border: '1px solid #e0e0e0', display: 'flex', gap: 1.5, alignItems: 'center' }}>
                                                    <Box
                                                        component="img"
                                                        src={getCategoryImageUrl(subcat)}
                                                        alt={subcat.name}
                                                        sx={{
                                                            width: 40,
                                                            height: 40,
                                                            objectFit: 'cover',
                                                            borderRadius: 1,
                                                            border: '1px solid #e0e0e0',
                                                            backgroundColor: '#fff'
                                                        }}
                                                    />
                                                    <Box flex={1}>
                                                        <Typography variant="subtitle2" component="div">
                                                            {subcat.name}
                                                        </Typography>
                                                        <Stack direction="row" spacing={1} alignItems="center">
                                                            <Chip size="small" label={`${subcat.productCount || 0}`} sx={{ height: 16, fontSize: '0.6rem', minWidth: 20 }} />
                                                            <Typography variant="caption" color={subcat.isActive ? 'success.main' : 'text.disabled'}>
                                                                {subcat.isActive ? 'Active' : 'Inactive'}
                                                            </Typography>
                                                        </Stack>
                                                    </Box>
                                                    <Box display="flex">
                                                        <IconButton size="small" onClick={() => handleOpenDialog(subcat)} sx={{ color: '#ff2852' }}>
                                                            <Edit fontSize="small" />
                                                        </IconButton>
                                                        <IconButton size="small" onClick={() => handleDelete(subcat._id)} sx={{ color: '#d32f2f' }}>
                                                            <Delete fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                </Paper>
                                            ))}
                                            {subcategories.length === 0 && (
                                                <Typography variant="body2" color="text.secondary" align="center" py={2}>
                                                    No subcategories found.
                                                </Typography>
                                            )}
                                        </Stack>
                                    </Box>
                                </Collapse>
                            </Card>
                        );
                    })}
                </Stack>
            ) : (
                <Paper sx={{ overflow: 'hidden' }}>
                    <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)' }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ width: '40px', p: 1 }} />
                                    <TableCell sx={{ minWidth: 60 }}>Image</TableCell>
                                    <TableCell sx={{ minWidth: 150 }}>Name</TableCell>
                                    <TableCell sx={{ minWidth: 100 }}>Type</TableCell>
                                    <TableCell sx={{ minWidth: 200 }}>Description</TableCell>
                                    <TableCell sx={{ minWidth: 80 }}>Products</TableCell>
                                    <TableCell sx={{ minWidth: 90 }}>Status</TableCell>
                                    <TableCell sx={{ minWidth: 80 }}>Sort</TableCell>
                                    <TableCell sx={{ minWidth: 120 }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {parentCategories.map((category) => {
                                    const subcategories = getSubcategories(category._id);
                                    const isExpanded = expandedCategories[category._id];
                                    const hasSubcategories = subcategories.length > 0;

                                    return (
                                        <React.Fragment key={category._id}>
                                            {/* Parent Row */}
                                            <TableRow sx={{ backgroundColor: '#fafafa !important' }}>
                                                <TableCell sx={{ p: 1 }}>
                                                    {hasSubcategories && (
                                                        <IconButton size="small" onClick={() => toggleExpand(category._id)} sx={{ p: 0.5 }}>
                                                            {isExpanded ? <ExpandLess /> : <ExpandMore />}
                                                        </IconButton>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Box component="img" src={getCategoryImageUrl(category)} alt={category.name} sx={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 1, border: '1px solid #e0e0e0', backgroundColor: '#f5f5f5' }} />
                                                </TableCell>
                                                <TableCell>
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        <Typography variant="subtitle2" fontWeight={600}>{category.name}</Typography>
                                                        {hasSubcategories && <Chip label={`${subcategories.length}`} size="small" sx={{ fontSize: '0.65rem', height: 20, minWidth: 24 }} />}
                                                    </Box>
                                                </TableCell>
                                                <TableCell><Chip label="Parent" color="primary" size="small" /></TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>{category.description?.substring(0, 30) || '-'}{category.description?.length > 30 && '...'}</Typography>
                                                </TableCell>
                                                <TableCell><Chip label={category.productCount || 0} size="small" /></TableCell>
                                                <TableCell><Chip label={category.isActive ? 'Active' : 'Inactive'} color={category.isActive ? 'success' : 'default'} size="small" /></TableCell>
                                                <TableCell><Typography variant="body2">{category.sortOrder || 0}</Typography></TableCell>
                                                <TableCell sx={{ p: 0.5 }}>
                                                    {tabValue === 1 && category.status === 'pending' && (
                                                        <>
                                                            <IconButton onClick={() => handleVerify(category._id, 'approve')} size="small" title="Approve" sx={{ color: 'green' }}><CheckCircle fontSize="small" /></IconButton>
                                                            <IconButton onClick={() => handleVerify(category._id, 'reject')} size="small" title="Reject" sx={{ color: 'red' }}><Block fontSize="small" /></IconButton>
                                                        </>
                                                    )}
                                                    {tabValue === 0 && (
                                                        <IconButton onClick={() => handleAddSubcategory(category)} sx={{ color: '#4caf50' }} size="small" title="Add Subcategory"><AddCircleOutline fontSize="small" /></IconButton>
                                                    )}
                                                    <IconButton onClick={() => handleOpenDialog(category)} sx={{ color: '#ff2852' }} size="small"><Edit fontSize="small" /></IconButton>
                                                    <IconButton onClick={() => handleDelete(category._id)} color="error" size="small"><Delete fontSize="small" /></IconButton>
                                                </TableCell>
                                            </TableRow>

                                            {/* Subcategories */}
                                            {isExpanded && subcategories.map((subcat) => (
                                                <TableRow key={subcat._id}>
                                                    <TableCell />
                                                    <TableCell>
                                                        <Box component="img" src={getCategoryImageUrl(subcat)} alt={subcat.name} sx={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 1, border: '1px solid #e0e0e0', backgroundColor: '#f5f5f5' }} />
                                                    </TableCell>
                                                    <TableCell sx={{ pl: 3 }}>
                                                        <Box display="flex" alignItems="center" gap={1}>
                                                            <SubdirectoryArrowRight sx={{ color: '#999', fontSize: 16 }} />
                                                            <Typography variant="body2">{subcat.name}</Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell><Chip label="Sub" color="secondary" size="small" variant="outlined" /></TableCell>
                                                    <TableCell><Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>{subcat.description?.substring(0, 30) || '-'}{subcat.description?.length > 30 && '...'}</Typography></TableCell>
                                                    <TableCell><Chip label={subcat.productCount || 0} size="small" /></TableCell>
                                                    <TableCell><Chip label={subcat.isActive ? 'Active' : 'Inactive'} color={subcat.isActive ? 'success' : 'default'} size="small" /></TableCell>
                                                    <TableCell><Typography variant="body2">{subcat.sortOrder || 0}</Typography></TableCell>
                                                    <TableCell sx={{ p: 0.5 }}>
                                                        {tabValue === 1 && (
                                                            <>
                                                                <IconButton onClick={() => handleVerify(subcat._id, 'approve')} size="small" title="Approve" sx={{ color: 'green' }}><CheckCircle fontSize="small" /></IconButton>
                                                                <IconButton onClick={() => handleVerify(subcat._id, 'reject')} size="small" title="Reject" sx={{ color: 'red' }}><Block fontSize="small" /></IconButton>
                                                            </>
                                                        )}
                                                        <IconButton onClick={() => handleOpenDialog(subcat)} sx={{ color: '#ff2852' }} size="small"><Edit fontSize="small" /></IconButton>
                                                        <IconButton onClick={() => handleDelete(subcat._id)} color="error" size="small"><Delete fontSize="small" /></IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </React.Fragment>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}

            {/* Dialog */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                <DialogTitle>
                    {editingCategory ? 'Edit Category' : (addingSubcategoryFor ? `Add Subcategory to "${addingSubcategoryFor.name}"` : 'Add New Category')}
                </DialogTitle>
                <form onSubmit={handleSubmit}>
                    <DialogContent dividers sx={{ p: { xs: 2, md: 3 } }}>
                        <Grid container spacing={3}>
                            {/* Image Upload */}
                            <Grid item xs={12}>
                                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>Category Image</Typography>
                                <Box sx={{ border: '2px dashed #ff2852', borderRadius: 2, p: 3, textAlign: 'center', backgroundColor: '#fafafa', cursor: 'pointer', transition: 'all 0.3s ease', '&:hover': { borderColor: '#5A189A', backgroundColor: '#f5f5f5' } }}
                                    onClick={() => document.getElementById('category-image-input').click()}>
                                    <input id="category-image-input" type="file" accept="image/*" onChange={handleImageChange} style={{ display: 'none' }} />
                                    {imagePreview ? (
                                        <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                            <Box component="img" src={imagePreview} alt="Preview" sx={{ maxWidth: 200, maxHeight: 150, objectFit: 'cover', borderRadius: 1, border: '1px solid #ddd' }} />
                                            <IconButton size="small" sx={{ position: 'absolute', top: -8, right: -8, backgroundColor: '#fff', boxShadow: 2 }}
                                                onClick={(e) => { e.stopPropagation(); setImagePreview(null); setImageFile(null); }}>
                                                <Cancel fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    ) : (
                                        <>
                                            <CloudUpload sx={{ fontSize: 48, color: '#ff2852', mb: 2 }} />
                                            <Typography variant="body1" sx={{ mb: 1 }}>Click to upload</Typography>
                                            <Typography variant="caption" color="text.secondary">PNG, JPG up to 5MB</Typography>
                                        </>
                                    )}
                                </Box>
                            </Grid>

                            {/* Form Fields */}
                            <Grid item xs={12} md={6}><TextField fullWidth label="Name *" name="name" value={formData.name} onChange={handleNameChange} required /></Grid>
                            <Grid item xs={12} md={6}><TextField fullWidth label="Slug *" name="slug" value={formData.slug} onChange={handleChange} required /></Grid>
                            <Grid item xs={12}><TextField fullWidth label="Description" name="description" value={formData.description} onChange={handleChange} multiline rows={3} /></Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Parent Category</InputLabel>
                                    <Select name="parentCategory" value={formData.parentCategory} onChange={handleChange} label="Parent Category" disabled={!!addingSubcategoryFor}>
                                        <MenuItem value="">None (Parent)</MenuItem>
                                        {parentCategories.map((cat) => <MenuItem key={cat._id} value={cat._id}>{cat.name}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}><TextField fullWidth label="Sort Order" name="sortOrder" type="number" value={formData.sortOrder} onChange={handleChange} inputProps={{ min: 0 }} /></Grid>

                            {/* SEO & Keywords */}
                            <Grid item xs={12}><Divider sx={{ my: 2 }}><Chip label="SEO & Keywords" /></Divider></Grid>
                            <Grid item xs={12}><FormControlLabel control={<Switch checked={formData.isActive} onChange={handleChange} name="isActive" />} label="Active" /></Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions sx={{ p: 3 }}>
                        <Button onClick={handleCloseDialog} startIcon={<Cancel />}>Cancel</Button>
                        <Button type="submit" variant="contained" startIcon={<Save />} disabled={uploadingImage || isLoading} sx={{ backgroundColor: '#ff2852', color: '#000' }}>
                            {uploadingImage ? <><CircularProgress size={20} sx={{ mr: 1 }} />Saving...</> : editingCategory ? 'Update' : 'Create'}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
        </Box>
    );
};

export default Categories;
